/// <amd-module name="Wit/Common/ColumnOptionsPanel/CustomRollupConfigurationPanel" />
import * as React from "react";
import { IBacklogLevelConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { RollupAggregation, RollupCalculationSettings, RollupType } from "Wit/Common/BacklogContentService/RollupHelpers";
import * as Resources from "Wit/Common/ColumnOptionsPanel/Resources";
import { WorkItemTypeColorAndIcon } from "Wit/UI/WorkItemTypeIcon";
import { AggregationModeControl, AggregationSelection } from "./AggregationModeControl";
import { BacklogLevelWorkItemTypePicker, BacklogLevelWorkItemTypePickerSelection } from "./BacklogLevelWorkItemTypePicker";

export interface ICustomRollupConfigurationPanelProps {
    onDismiss: () => void;
    onSubmit: (rollupCalculationSettings: RollupCalculationSettings, friendlyNames?: { backlogName?: string; fieldName?: string }) => void;
}
interface CustomRollupPanelState {
    friendlyFilterName?: string;
    friendlyFieldName?: string;
    hasValidAggregationSelection: boolean;
    hasValidBacklogOrWorkItemSelection: boolean;
    rollupCalculation: RollupCalculationSettings;
}

export class CustomRollupConfigurationPanel extends VssComponent<ICustomRollupConfigurationPanelProps, CustomRollupPanelState> {
    onDismiss: () => void;

    private selectedRadioButton = new ObservableValue<string>(Resources.Progress);

    public constructor(props: ICustomRollupConfigurationPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            rollupCalculation: {
                aggregation: RollupAggregation.Count,
                type: RollupType.Progress
            },
            hasValidAggregationSelection: true,
            hasValidBacklogOrWorkItemSelection: false,
            friendlyFilterName: ""
        };
    }

    public render() {
        return (
            <Panel
                className="column-options-panel flex-column"
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.CustomRollupPanelTitle }}
                footerButtonProps={[
                    { text: Resources.OK, primary: true, onClick: this.onSubmit },
                    { text: Resources.Cancel, onClick: this.props.onDismiss }
                ]}
            >
                <div className="flex-column">
                    {this.renderRollupTypeControl()}
                    {this.renderWorkItemTypePicker()}
                    {this.renderAggregationModePicker()}
                </div>
            </Panel>
        );
    }

    private renderRollupTypeControl() {
        const onSelect = (selectedButtonId: string) => {
            if (selectedButtonId === Resources.Progress) {
                this.state.rollupCalculation.type = RollupType.Progress;
                this.selectedRadioButton.value = selectedButtonId;
            } else {
                this.state.rollupCalculation.type = RollupType.Total;
                this.selectedRadioButton.value = selectedButtonId;
            }
        };
        return (
            <RadioButtonGroup onSelect={onSelect} selectedButtonId={this.selectedRadioButton} text={Resources.SelectRollupColumn}>
                <RadioButton id={Resources.Progress} ariaLabelledBy={Resources.Progress}>
                    <div className="bolt-radio-button-label">
                        {Resources.RollupByProgress}
                        <span className="sub-text-class">{Resources.CustomRollupProgressSubtitle}</span>
                    </div>
                </RadioButton>
                <RadioButton id={Resources.Total} ariaLabelledBy={Resources.Total}>
                    <div className="bolt-radio-button-label">
                        {Resources.RollupByTotal}
                        <span className="sub-text-class">{Resources.CustomRollupTotalSubtitle}</span>
                    </div>
                </RadioButton>
            </RadioButtonGroup>
        );
    }

    private renderWorkItemTypePicker() {
        return (
            <div className="padding-16">
                <span className="padding-bottom-8">{Resources.WorkItemTypePickerLabel}</span>
                <BacklogLevelWorkItemTypePicker onSelectionChanged={this.onWorkItemSelectionChanged} />
            </div>
        );
    }

    private onSubmit = () => {
        this.props.onDismiss();
        this.props.onSubmit(this.state.rollupCalculation, { backlogName: this.state.friendlyFilterName, fieldName: this.state.friendlyFieldName });
    };

    private onWorkItemSelectionChanged = (selection: BacklogLevelWorkItemTypePickerSelection): void => {
        if (selection) {
            const newState = {
                rollupCalculation: {
                    ...this.state.rollupCalculation
                },
                friendlyFilterName: "",
                hasValidBacklogOrWorkItemSelection: true
            };
            const selectionCast: IBacklogLevelConfiguration = selection as IBacklogLevelConfiguration;
            if (selectionCast.id) {
                newState.rollupCalculation.backlogFilter = selectionCast.id;
                // work item type filter and backlog filter are mutually exclusive;
                // clear out work item type filter if we update to use backlog filter
                newState.rollupCalculation.workItemTypeFilter = undefined;
                newState.friendlyFilterName = selectionCast.name;
            } else {
                const typeSelection: WorkItemTypeColorAndIcon = selection as WorkItemTypeColorAndIcon;
                newState.rollupCalculation.workItemTypeFilter = typeSelection.workItemTypeName;
                // work item type filter and backlog filter are mutually exclusive;
                // clear out backlog filter if we update to use work item type filter
                newState.rollupCalculation.backlogFilter = undefined;
            }
            this.setState(newState);
        }
    };

    private renderAggregationModePicker() {
        return (
            <AggregationModeControl
                workItemTypeName={this.state.rollupCalculation.workItemTypeFilter}
                backlogLevelId={this.state.rollupCalculation.backlogFilter}
                onAggregationSelectionChanged={this.onAggregationSelectionChanged}
                defaultAggregationMode={RollupAggregation.Count}
            />
        );
    }

    private onAggregationSelectionChanged = (aggregationSelection: AggregationSelection, isValidField: boolean): void => {
        const newRollupCalculation: RollupCalculationSettings = {
            ...this.state.rollupCalculation,
            ...aggregationSelection
        };
        if (aggregationSelection.aggregation === RollupAggregation.Count) {
            // clear out aggregation field since only Sum requires an aggregation field
            newRollupCalculation.aggregationField = undefined;
        }
        this.setState({
            rollupCalculation: newRollupCalculation,
            hasValidAggregationSelection: isValidField,
            friendlyFieldName: aggregationSelection.aggregationFieldDisplayName
        });
    };
}
